import { c as _c } from "react/compiler-runtime";
import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { FaroTextButton } from "@components/common/faro-text-button";
import { useDeleteProjectBulkDialogContent } from "@components/table/projects/bulk-actions/bulk-delete-project-helper";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { deleteProject } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { bulkActionNameSelector, selectedEntitiesSelector, shouldShowSuccessDialogSelector } from "@store/table/table-selector";
import { setBulkActionName, updateOne } from "@store/table/table-slice";
import { RequiredPermissionProjectLevelName } from "@utils/permission-control/project-permission-control-types";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect, useState } from "react";
import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";
import { Text, DeleteIcon } from "@flat-ui";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";

/** Show delete projects button and dispatch action where the bulk dialog will act accordingly */
export function BulkDeleteProjects(t0) {
  const $ = _c(34);
  const {
    companyId
  } = t0;
  const dispatch = useAppDispatch();
  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const shouldShowSuccessDialog = useAppSelector(shouldShowSuccessDialogSelector);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = selectedEntitiesSelector("projects");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const selectedEntities = useAppSelector(t1);
  const {
    hasUserPermissionProjectLevel
  } = useHasUserValidPermissionProjectLevel();
  const {
    bulkDialogContent,
    successDialogContent
  } = useDeleteProjectBulkDialogContent();
  const mode = useBulkActionsMode();
  let t2;
  let t3;
  if ($[1] !== shouldShowSuccessDialog) {
    t2 = () => {
      if (shouldShowSuccessDialog) {
        setShouldShowDialog(false);
      }
    };
    t3 = [shouldShowSuccessDialog];
    $[1] = shouldShowSuccessDialog;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== dispatch || $[5] !== hasUserPermissionProjectLevel || $[6] !== selectedEntities) {
    t4 = function onDeleteProjectClick() {
      dispatch(setBulkActionName("removeProject"));
      for (const entity of selectedEntities) {
        const hasUserValidRole = hasUserPermissionProjectLevel({
          permissionName: RequiredPermissionProjectLevelName.canDeleteProject,
          project: entity
        });
        if (!hasUserValidRole) {
          dispatch(updateOne({
            id: entity.id,
            changes: {
              status: "not-allowed",
              message: "You are not allowed to delete this project from workspace"
            }
          }));
        }
      }
      setShouldShowDialog(true);
    };
    $[4] = dispatch;
    $[5] = hasUserPermissionProjectLevel;
    $[6] = selectedEntities;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const onDeleteProjectClick = t4;
  let t5;
  if ($[8] !== mode) {
    t5 = getBulkActionButtonConfig(mode, "Delete project from workspace");
    $[8] = mode;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const config = t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <DeleteIcon sx={{
      width: "18px",
      height: "18px"
    }} />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== config.shouldShowText) {
    t7 = config.shouldShowText && <Text variant="buttonM">Delete</Text>;
    $[11] = config.shouldShowText;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== config.rippleColor || $[14] !== config.sx || $[15] !== config.tooltipText || $[16] !== onDeleteProjectClick || $[17] !== t7) {
    t8 = <FaroTextButton onClick={onDeleteProjectClick} tooltipText={config.tooltipText} startIcon={t6} rippleColor={config.rippleColor} sx={config.sx}>{t7}</FaroTextButton>;
    $[13] = config.rippleColor;
    $[14] = config.sx;
    $[15] = config.tooltipText;
    $[16] = onDeleteProjectClick;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== bulkActionName || $[20] !== shouldShowSuccessDialog || $[21] !== successDialogContent) {
    t9 = bulkActionName === "removeProject" && shouldShowSuccessDialog && <BulkSuccessDialog dialogContent={successDialogContent} />;
    $[19] = bulkActionName;
    $[20] = shouldShowSuccessDialog;
    $[21] = successDialogContent;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== bulkActionName || $[24] !== bulkDialogContent || $[25] !== companyId || $[26] !== dispatch || $[27] !== selectedEntities || $[28] !== shouldShowDialog) {
    t10 = bulkActionName === "removeProject" && shouldShowDialog && <FaroBulkDialog dialogContent={bulkDialogContent} onClose={() => setShouldShowDialog(false)} trackingEvent={BulkActionEvents.bulkDeleteProject} bulkActionCallback={project => dispatch(deleteProject({
      companyId,
      projectId: project.id
    })).unwrap()} selectedEntities={selectedEntities} uniqueIdKey="id" tableDataType="projects" dialogVariant="danger" />;
    $[23] = bulkActionName;
    $[24] = bulkDialogContent;
    $[25] = companyId;
    $[26] = dispatch;
    $[27] = selectedEntities;
    $[28] = shouldShowDialog;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t8 || $[32] !== t9) {
    t11 = <>{t8}{t9}{t10}</>;
    $[30] = t10;
    $[31] = t8;
    $[32] = t9;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  return t11;
}